import { useEffect, useRef, useState } from "react";
import { MessageCircleMore, Sparkles } from "lucide-react";
import { HIGHLIGHT_CONFIG } from "./highlights";
import { useNotebookContext } from "../../../hooks/useNotebook";
import { HighlightButton } from "./highlight-button";
import { cn } from "@/lib/utils";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import {
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Tooltip,
} from "@/components/ui/tooltip";

type HighlightCategory = {
  count: number;
  config: (typeof HIGHLIGHT_CONFIG)[number];
};

// MOCK DATA for display/testing purposes when no real highlights exist
// const MOCK_CATEGORIES: HighlightCategory[] = [
//   {
//     count: 12,
//     config: HIGHLIGHT_CONFIG[0],
//   },
//   {
//     count: 3,
//     config: HIGHLIGHT_CONFIG[1],
//   },
//   {
//     count: 5,
//     config: HIGHLIGHT_CONFIG[2],
//   },
//   {
//     count: 8,
//     config: HIGHLIGHT_CONFIG[0], // Reusing flashcard config for styling
//   },
//   {
//     count: 9,
//     config: HIGHLIGHT_CONFIG[1], // Reusing flashcard config for styling
//   },
//   {
//     count: 8,
//     config: HIGHLIGHT_CONFIG[0], // Reusing flashcard config for styling
//   },
//   {
//     count: 9,
//     config: HIGHLIGHT_CONFIG[1], // Reusing flashcard config for styling
//   },
// ];

export function HighlightPanel() {
  const { notebook } = useNotebookContext();
  const [categories, setCategories] = useState<HighlightCategory[]>([]);

  const { highlights, currentPage, navigatePage } = useNotebookContext();
  const isLoading = false;

  const selectedHighlightCategory =
    currentPage.type === "highlight_category" ? currentPage.value : null;

  const selectedHighlight =
    currentPage.type === "highlight" ? currentPage.value : null;

  const selectHighlightCategory = (
    category: HighlightCategory["config"]["id"]
  ) => navigatePage({ type: "highlight_category", value: category });

  useEffect(() => {
    const highlightCategories = HIGHLIGHT_CONFIG.map((c) => ({
      count: highlights.filter((h) => h.type === c.id).length ?? 0,
      config: c,
    })).filter((c) => c.count > 0);

    setCategories(highlightCategories);
  }, [highlights]);

  const prevCountsRef = useRef<Record<string, number>>({});

  // Validate notebook.id exists
  if (!notebook?.id) {
    return (
      <div className="flex items-center justify-center p-4 bg-theme-1 mt-3 mx-1 rounded-lg h-40">
        <div className="text-sm text-theme-2">No study set selected</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4 bg-theme-1 mt-3 mx-1 rounded-lg h-40">
        <Sparkles className="h-5 text-theme-2 mr-1" />
        <div className="text-sm text-theme-2 animate-pulse">
          Loading saved materials...
        </div>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="flex items-center justify-center p-4 text-theme-2 bg-theme-1 mt-3 mx-1 rounded-lg h-40 animate-in fade-in duration-300 group-data-[collapsible=icon]:hidden">
        <p className="text-sm max-w-xs text-center">
          <MessageCircleMore className="h-5 w-5 inline mr-[6px]" />
          <span className="group-data-[collapsible=icon]:hidden">
            No saved materials yet. Start creating from chat!
          </span>
        </p>
      </div>
    );
  }

  //   TEMPORARILY using mock data
  //   const mockcategories = MOCK_CATEGORIES;

  // Determine grid columns: 1 column for 1-3 categories, 2 columns for 4+
  const gridCols = categories.length <= 3 ? "grid-cols-1" : "grid-cols-2";

  // Calculate dynamic height based on number of rows in 2-column layout
  const getButtonHeight = () => {
    if (categories.length <= 3) {
      return "py-6"; // Default padding for single column
    }

    const numRows = Math.ceil(categories.length / 2);

    if (numRows === 2) return "py-12"; // Larger buttons for 2 rows (4-5 categories)
    if (numRows === 3) return "py-8"; // Medium buttons for 3 rows (6-7 categories)
    return "py-6"; // Smaller buttons for 4+ rows (8+ categories)
  };

  const buttonHeight = getButtonHeight();

  return (
    <SidebarGroupContent>
      <SidebarMenu>
        <div
          className={`grid ${gridCols} gap-2 group-data-[collapsible=icon]:gap-4 px-2 group-data-[collapsible=icon]:px-1 pt-2 group-data-[collapsible=icon]:grid-cols-1`}
        >
          <style>{`
  @keyframes count-pop {
    0% {
      transform: scale(1);
      filter: brightness(1);
    }
    40% {
      transform: scale(1.35);
      filter: brightness(1.2);
    }
    100% {
      transform: scale(1);
      filter: brightness(1);
    }
  }

  .animate-count-pop {
    animation: count-pop 500ms cubic-bezier(0.2, 0.8, 0.3, 1);
  }
`}</style>

          {categories.map((category) => {
            const prevCount =
              prevCountsRef.current[category.config.id] ?? category.count;

            const didIncrease = category.count > prevCount;

            return (
              <TooltipProvider delayDuration={200} key={category.config.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <SidebarMenuItem>
                      <div key={category.config.id} className="relative">
                        <HighlightButton
                          highlight={category.config}
                          isActive={
                            selectedHighlightCategory === category.config.id
                          }
                          onSelect={() =>
                            selectHighlightCategory(category.config.id)
                          }
                          className={cn(
                            "flex items-center justify-center gap-2 w-full px-3 group-data-[collapsible=icon]:px-4 rounded-xl group-data-[collapsible=icon]:rounded-full transition-all",
                            buttonHeight,

                            // dim unselected options only in this intermediate state
                            (selectedHighlight ||
                              (selectedHighlightCategory &&
                                selectedHighlightCategory !==
                                  category.config.id)) &&
                              "opacity-40 hover:opacity-80"
                          )}
                        />

                        {category.count > 0 && (
                          <span
                            className={`
            absolute -right-2 -top-2 flex h-5 w-5
            items-center justify-center rounded-full
            bg-theme-0 text-xs font-semibold text-theme-2 ring-2
            ${didIncrease ? "animate-count-pop" : ""}
          `}
                          >
                            {category.count}
                          </span>
                        )}
                      </div>
                    </SidebarMenuItem>
                  </TooltipTrigger>
                  <TooltipContent side="right" align="center">
                    <span className="text-xs">
                      View {category.config.labelLong}
                    </span>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>
      </SidebarMenu>
    </SidebarGroupContent>
  );
}
